import { useState } from "react";
import { api } from "@/api/client";
import { History, AlertCircle } from "lucide-react";

/** NJM OS Interaction Event Layer, Phase 2 (2026-08) — a small, read-only "recent activity"
 * trail shared across assignment (staff + freelancer) and collaboration pages. The backend
 * endpoint already returns only the events this viewer is allowed to see (see
 * kernel/interaction_events.py) — this component just renders whatever it gets, no client-side
 * filtering. Re-fetches every time it's expanded (not just once) so a note/transition posted
 * earlier in the same session shows up without needing a full page remount. */
export function ActivityTrail({ endpoint, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    api.get(endpoint)
      .then((r) => setEvents(r.data.items || []))
      .catch(() => { setEvents(null); setError(true); });
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  return (
    <div className="mt-3">
      <button onClick={toggle} data-testid={`${testIdPrefix}-activity-toggle`}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-soft hover:text-ink transition-colors">
        <History className="h-3 w-3" /> Recent Activity{events ? ` (${events.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`${testIdPrefix}-activity-list`}>
          {error ? (
            <div className="flex items-center gap-1.5 text-[11px] text-red-600" data-testid={`${testIdPrefix}-activity-error`}>
              <AlertCircle className="h-3 w-3" /> Couldn't load activity. Try again.
            </div>
          ) : events === null ? (
            <div className="text-[11px] text-faint">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-[11px] text-faint" data-testid={`${testIdPrefix}-activity-empty`}>No activity recorded yet.</div>
          ) : events.map((e) => (
            <div key={e.id} className="rounded-md border border-hairline bg-surface2/40 px-3 py-1.5" data-testid={`activity-event-${e.id}`}>
              <p className="text-[11.5px] text-ink">{e.summary}</p>
              <span className="text-[10px] text-faint">{e.at ? new Date(e.at).toLocaleString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
